/**
 * V2Layout — V3 sidebar + 48 px top bar + cream page surface.
 *
 * Children get the canonical `#F5F3EF` canvas, scrollable beneath the
 * top bar.
 *
 * Mobile (<= 768 px):
 *   • Sidebar is fixed-position and slides in from the start edge.
 *   • A hamburger button appears in the top bar; tapping it toggles the
 *     sidebar; tapping any nav item or the dimmer closes it.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { Sidebar } from '@/components/Sidebar';
import { TopBar } from '@/components/TopBar';
import { ReauthBanner } from '@/components/auth/ReauthBanner';
import { useLanguage } from '@/contexts/LanguageContext';
import { T } from '@/lib/terminology';
import { cn } from '@/lib/utils';

const MOBILE_BREAKPOINT_PX = 768;

const breadcrumbFor = (path, t) => {
  if (path.startsWith('/chat/vault') || path.startsWith('/v2/vault')) return t.vault;
  if (path.startsWith('/chat/workflows') || path.startsWith('/v2/workflows')) return t.workflows;
  if (path.startsWith('/chat') || path.startsWith('/v2')) return t.assistant;
  if (path.startsWith('/templates')) return t.templates;
  if (path.startsWith('/guidance')) return t.guidance;
  if (path.startsWith('/settings')) return t.settings;
  if (path.startsWith('/library')) return t.draftLibrary;
  return '';
};

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT_PX,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setIsNarrow(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isNarrow;
}

export function V2Layout({ children }) {
  const { language } = useLanguage();
  const location = useLocation();
  const isArabic = language === 'ar';
  const t = T(language);
  const isNarrow = useIsNarrow();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the mobile sidebar on route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search, location.hash]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const toggleMobile = useCallback(() => setMobileOpen((o) => !o), []);

  return (
    <div
      className={cn('flex h-screen w-full', isArabic && 'flex-row-reverse')}
      data-theme="harvey"
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="v2-layout"
      /* Jul 2026 mobile fix: h-screen (100vh) exceeds the VISIBLE
         viewport on iOS — the browser parks the 48px TopBar under the
         URL bar in some scroll states (header "disappears") and pushes
         the footer disclaimer off-screen. 100dvh tracks the real
         visible height; the inline style overrides h-screen where
         supported, and old browsers fall back to the class. */
      style={{ background: 'var(--canvas)', height: '100dvh' }}
    >
      <Sidebar
        isNarrow={isNarrow}
        mobileOpen={mobileOpen}
        onMobileClose={closeMobile}
      />
      {/* Mobile dimmer — only visible while the drawer is open on a narrow viewport. */}
      {isNarrow && mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={closeMobile}
          data-testid="v2-layout-mobile-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 49,
            border: 0,
            padding: 0,
            margin: 0,
            cursor: 'pointer',
          }}
        />
      )}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        <TopBar
          breadcrumb={breadcrumbFor(location.pathname, t)}
          showMenuButton={isNarrow}
          onMenuToggle={toggleMobile}
        />
        {/* Cluster A: persistent re-auth banner. Surfaces only when
            apiFetch detects a hard 401 after a refresh attempt;
            queued API calls resume after the user re-authenticates. */}
        <ReauthBanner />
        <main
          className="flex-1 min-h-0 overflow-auto"
          data-testid="v2-layout-main"
          style={{ background: 'var(--canvas)' }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

export default V2Layout;
